import {THEME} from '@wandb/weave/common/css/color.styles';
import {GOLD_300} from '@wandb/weave/common/css/globals.styles';
import {type IconName} from '@wandb/weave/components/Icon';
import _ from 'lodash';
import React, {useState} from 'react';
import {Label} from 'semantic-ui-react';

import type * as GQLTypes from '../types/graphql';
import {regexMatchHighlight} from '../util/fuzzyMatch';
import {SingleLineText} from './elements/Text';
import * as S from './Tags.styles';

export enum TagType {
  TAG,
  ALIAS,
  PROTECTED_ALIAS,
}

interface TagProps {
  size?: 'large' | 'medium' | 'small';
  tag: GQLTypes.Tag;
  noun?: 'tag' | 'alias' | 'protected-alias';
  canDelete?: boolean;
  showColor?: boolean;
  highlightedText?: string;
  disableHover?: boolean;
  showIcon?: boolean;
  style?: React.CSSProperties;

  onDelete?(e: React.MouseEvent<HTMLElement>): void;
  onClick?(): void;
}

function nounToTagType(noun: string): TagType {
  switch (noun) {
    case 'tag':
      return TagType.TAG;
    case 'alias':
      return TagType.ALIAS;
    case 'protected-alias':
      return TagType.PROTECTED_ALIAS;
    default:
      return TagType.TAG;
  }
}

function colorIndexToName(showColor: boolean, index?: number): string {
  if (!showColor) {
    return 'tag-lightGray';
  }
  switch (index) {
    case TagType.TAG:
      return 'tag-teal-light';
    case TagType.ALIAS:
      return 'tag-sienna-light';
    case TagType.PROTECTED_ALIAS:
      return 'tag-lightGray';
    default:
      return 'tag-lightGray';
  }
}

function nounToIconName(noun: string): IconName {
  switch (noun) {
    case 'tag':
      return 'tag';
    case 'alias':
      return 'email-at';
    case 'protected-alias':
      return 'lock-closed';
    default:
      return 'email-at';
  }
}

export const Tag: React.FC<TagProps> = ({
  size: propsSize,
  tag,
  noun,
  canDelete,
  showColor,
  onDelete,
  onClick,
  highlightedText,
  disableHover,
  showIcon = true,
  style,
}) => {
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const size = propsSize || 'large';
  noun = noun ?? 'tag';
  canDelete = canDelete ?? true;
  showColor = showColor ?? true;
  let highlightedTextRegex = null;
  try {
    if (highlightedText) {
      highlightedTextRegex = new RegExp(highlightedText, 'i');
    }
  } catch (error) {
    console.warn('Error parsing highlighted text in Tag: ', error);
  }

  // As per new design guidelines, colors for tags and aliases are no longer random
  // Since they've been fixed based on tag type (aka `noun`), the color assignment here will ignore
  // the database color index. This can be modified in the future if the design decision changes
  // based on user feedback
  const colorName = colorIndexToName(showColor, nounToTagType(noun));
  const noHoverClass = disableHover ? 'no-hover' : '';
  return (
    <Label
      style={{marginLeft: '2px', maxWidth: '220px', ...style}}
      className={
        showDeleteAlert
          ? `run-tag ${size} tag-red-alert ${noHoverClass}`
          : `run-tag ${size} ${colorName} ${noHoverClass}`
      }
      key={tag.name}
      onClick={onClick}>
      {showIcon && (
        <S.StyledIcon name={nounToIconName(noun)} size={size} $pos="left" />
      )}
      <SingleLineText alignSelf={'center'}>
        {highlightedText
          ? regexMatchHighlight(tag.name, highlightedTextRegex, {
              backgroundColor: GOLD_300,
              color: THEME.textPrimary,
            })
          : tag.name}
      </SingleLineText>
      {canDelete && onDelete && (
        <S.StyledIcon
          className="delete-tag"
          name="close"
          size={size}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            onDelete(e);
          }}
          onMouseOver={() => setShowDeleteAlert(true)}
          onMouseOut={() => setShowDeleteAlert(false)}
          $pos="right"
          $opacity={0.6}
          $cursor="pointer"
        />
      )}
    </Label>
  );
};

interface TagsProps {
  size?: 'medium' | 'small';
  tags: GQLTypes.Tag[];
  enableDelete?: boolean;
  noun?: 'tag' | 'alias';

  deleteTag?(tag: GQLTypes.Tag): void;
  onClick?(tag: string): void;
  highlightedText?: string;
}

export const Tags: React.FC<TagsProps> = ({
  size,
  tags,
  enableDelete,
  noun,
  deleteTag,
  onClick,
  highlightedText,
}) => {
  return (
    <span className="run-tags">
      {_.sortBy(tags, 'name').map(tag => (
        <Tag
          key={tag.name}
          tag={tag}
          size={size}
          onDelete={
            enableDelete && deleteTag
              ? e => {
                  if (deleteTag) {
                    e.stopPropagation();
                    deleteTag(tag);
                  }
                }
              : undefined
          }
          onClick={() => onClick?.(tag.name)}
          noun={noun}
          highlightedText={highlightedText}
        />
      ))}
    </span>
  );
};
